/**
 * 承認事項 1 件の行。選択・操作・対象の 3 列。案件・種別・状態・提案者 / 日時は行の `title` に載せる。
 *
 * 表（`ApprovalTable`）と同じセルの余白を使うので、余白の定義はここに置いて表へ渡す。
 *
 * **提案理由と判断の履歴の列を持たない**（決定 3）。案件の変更の行に理由の欄は無く、
 * なぜ変えたかは要求と why の層が、誰がいつ判断したかは操作履歴が持つ。空になる列を残すと、
 * 「理由を書き忘れた提案」と「そもそも理由を持たない表」が画面で見分けられなくなる。
 *
 * **対象の行き先は操作で分かれる。** 新設と更新は行が属する案件を重ねた姿へ、削除は master の
 * 姿へ送る（`targetPjOf`）。案件が消す対象は重ねた姿にもう居ないので、重ねて開くと「見つかり
 * ません」に着き、承認する担当者は何を消そうとしているのかを確かめられない。
 *
 * **削除の行に「master」の印を付けない**（Dhik 2026-09-28。「master」は内部の実装の話）。削除の行で
 * あることは操作の列の「削除」の札で分かるので、ⓘ にも回さない。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 承認事項 1 件を選択・操作・対象の 3 列で描き、案件・種別・状態・提案者と日時を行の title に載せる。
 * @graph-connects approvalRowTitle [calls] 行の title
 * @graph-connects SpecNodeLinkInPj [calls] 行が属する案件を重ねた用語詳細 / マスター詳細への導線
 * @graph-connects targetPjOf [calls] 対象を開くときに重ねる案件
 */
import type { ApprovalKind, ApprovalRow as ApprovalItem } from '@cortex/domain-spec-console';
import { Checkbox } from '@cortex/web-design-system/components/ui/Checkbox';
import { useTranslation } from 'react-i18next';

import { SpecNodeLinkInPj } from '../../components/spec/SpecNodeLink.js';
import { displayLabel } from '../../components/spec/UntranslatedBadge.js';
import type { SpecNodeTypeValue } from '../../lib/spec-node-types.js';
import { approvalRowKey } from '../approvals-selection.js';

/**
 * 表のセルに共通の余白。行と見出し行で同じ値を使い、列の縦の揃いを保つ。
 * @graph-business 承認事項の表のセルに共通の余白。
 * @graph-connects none
 */
export const APPROVAL_CELL_CLASS = 'px-2 py-2 align-top';

/**
 * 行の対象を開くときに重ねる案件を決める。削除の行だけ master の姿へ送る。
 *
 * **案件が消す対象は、その案件を重ねた姿にはもう居ない。** 重ねて開くと必ず「見つかりません」に
 * 着き、承認する担当者は**何を消そうとしているのかを確かめられないまま判断する**ことになる
 * （実測で用語 590 / 617 の 2 行）。master なら消える前の姿がそのまま読める。
 *
 * 新設と更新は逆で、master に無い（新設）か古い（更新）ので案件を重ねる。
 *
 * @param item 承認事項 1 件
 * @returns 重ねる案件番号。master の姿へ送るなら undefined
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 承認事項の対象を開くとき、削除の行は master の姿へ、それ以外は行が属する案件を重ねた姿へ送る。
 * @graph-connects none
 */
export function targetPjOf(item: ApprovalItem): string | undefined {
  return item.operation === 'delete' ? undefined : String(item.pjNumber);
}

/** `ApprovalRow` の props。 @graph-connects none */
export interface ApprovalRowProps {
  item: ApprovalItem;
  selected: boolean;
  onToggle: (rowKey: string, selected: boolean) => void;
}

/**
 * 承認の種別から、行き先の判断が使うノードの型へ写す。
 *
 * **行き先の判断そのものは `resolveSpecNodeLink` が 1 箇所で持つ**（用語は用語詳細、アクター・
 * ドメイン・ステージはマスター詳細）。この画面が独自に行き先を組むと、詳細ルートが
 * 増えたときに「一覧からは飛べるが承認一覧からは飛べない」という説明の付かない差が生まれる。
 *
 * `Record` で持つので、承認の対象に種別が増えたときは対応を足すまで型検査が落ちる。
 * @graph-connects none
 */
const NODE_TYPE_BY_KIND: Readonly<Record<ApprovalKind, SpecNodeTypeValue>> = {
  term: 'Term',
  actor: 'Actor',
  domain: 'Domain',
  stage: 'Stage',
};

/**
 * 操作の札の見た目。追加は緑のベタ、変更は緑の枠、削除は赤のベタ（合意 `approvals.html`）。
 * @graph-connects none
 */
const OPERATION_CLASS: Readonly<Record<ApprovalItem['operation'], string>> = {
  create:
    'inline-block whitespace-nowrap rounded-sm border border-[var(--color-diff-added-pane)] bg-[var(--color-diff-added-pane)] px-1.5 py-0.5 text-xs text-[var(--color-bg-base)]',
  update:
    'inline-block whitespace-nowrap rounded-sm border border-[var(--color-diff-added-pane)] px-1.5 py-0.5 text-xs text-[var(--color-diff-added-pane)]',
  delete:
    'inline-block whitespace-nowrap rounded-sm border border-[var(--color-diff-removed-pane)] bg-[var(--color-diff-removed-pane)] px-1.5 py-0.5 text-xs text-[var(--color-bg-base)]',
};

/**
 * 行を選べるか。**未承認の行だけ選べる**（合意 `approvals.html`）。判断済みの行を選べると、
 * 承認済みを却下へ倒す操作が一覧の一括操作から紛れ込む。
 *
 * @param item 承認事項 1 件
 * @returns 選べるなら true
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 承認事項 1 件が一括の判断の対象として選べるか（未承認だけ）を決める。
 * @graph-connects none
 */
export function isSelectableApproval(item: ApprovalItem): boolean {
  return item.state === 'pending';
}

/**
 * 行の `title` — 案件 ・ 種別 ・ 状態 ・ 提案者と日時。判断に要らない補足なので列にせず、行に指を
 * 載せたときに読めるようにする（Dhik 2026-09-29「本当に出すべき情報を 1 or 2 個に」）。案件と種別は
 * 絞り込みで揃うと全行同じ値になり、状態は既定の絞り込み（未承認）で全行同じになる。
 * @param item 承認事項 1 件
 * @param t 文言
 * @returns 行の title
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 承認事項の行に指を載せたときに出す、案件 ・ 種別 ・ 状態 ・ 提案者と日時の 1 行を作る。
 * @graph-connects none
 */
export function approvalRowTitle(
  item: ApprovalItem,
  t: (key: string, params?: Record<string, unknown>) => string,
): string {
  return [
    `${t('approvals.colPj')}: ${String(item.pjNumber)}`,
    `${t('approvals.colKind')}: ${t(`approvals.kind.${item.kind}`)}`,
    `${t('approvals.colState')}: ${t(`approvals.state.${item.state}`)}`,
    `${t('approvals.colProposer')}: ${item.actor} ${item.updatedAt}`,
  ].join(' ・ ');
}

/**
 * 承認事項 1 件の行。選択・操作・対象の 3 列で、案件・種別・状態・提案者 / 日時は行の `title`
 * （`approvalRowTitle`）。
 *
 * 長い表示名（承認の対象の名前）は切り詰め、全文は `title` で読める。
 *
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 承認事項 1 件を選択・操作・対象の 3 列で描き、案件・種別・状態・提案者と日時を行の title に載せる。
 * @graph-connects approvalRowTitle [calls] 行の title
 * @graph-connects SpecNodeLinkInPj [calls] 行が属する案件を重ねた用語詳細 / マスター詳細への導線
 * @graph-connects targetPjOf [calls] 対象を開くときに重ねる案件
 * @graph-connects approvalRowKey [calls] 行を一意に指す鍵
 */
export function ApprovalRow({ item, selected, onToggle }: ApprovalRowProps) {
  const { t } = useTranslation();
  const rowKey = approvalRowKey(item);
  const label = displayLabel(item.label, t, item.untranslated);
  /* 名前はあるが訳の欠けた行の「未翻訳」は、名前の title に添える（名前が空なら本文が既に言っている）。 */
  const untranslated = item.label !== '' && item.untranslated;

  return (
    <tr
      className="border-b border-[var(--color-border)] last:border-b-0"
      data-approval-row={rowKey}
      data-term-change-state={item.state}
      title={approvalRowTitle(item, t)}
    >
      <td className={APPROVAL_CELL_CLASS}>
        <Checkbox
          aria-label={t('approvals.selectRow', { label })}
          checked={selected}
          disabled={!isSelectableApproval(item)}
          onChange={(event) => {
            onToggle(rowKey, event.target.checked);
          }}
        />
      </td>
      <td className={APPROVAL_CELL_CLASS}>
        <span className={OPERATION_CLASS[item.operation]} data-approval-operation={item.operation}>
          {t(`approvals.operation.${item.operation}`)}
        </span>
      </td>
      <td className={`${APPROVAL_CELL_CLASS} max-w-[16rem]`}>
        <div className="flex min-w-0 items-center gap-1">
          {/* 承認一覧は案件を跨ぐので現在地に案件が無い。行が属する案件を重ねて開かないと、
              その案件で新設した用語やマスターは master に無く「見つかりません」になる。
              削除の行だけは逆で、重ねた姿にはもう居ない。master へ送る（`targetPjOf`）。 */}
          <SpecNodeLinkInPj
            nodeType={NODE_TYPE_BY_KIND[item.kind]}
            nodeId={String(item.nodeId)}
            pjNumber={targetPjOf(item)}
            className="min-w-0 truncate text-sm text-[var(--color-accent-primary)] underline"
          >
            <span
              data-approval-target
              title={untranslated ? `${label} / ${t('spec.untranslated')}` : label}
            >
              {label}
            </span>
          </SpecNodeLinkInPj>
        </div>
      </td>
    </tr>
  );
}
